// Public demo token-login. Visitor lands here from the email link with
// `?token=…` populated; we auto-submit it. They can also paste the token
// manually if the magic link gets mangled by their mail client.

import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ApiError, demo } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { dirFor } from '@/i18n';

const SearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/demo/login')({
  component: DemoTokenLoginPage,
  validateSearch: (s) => SearchSchema.parse(s),
});

function DemoTokenLoginPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: '/demo/login' });
  const [token, setToken] = useState(search.token ?? '');
  const submittedAuto = useRef(false);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  // Disable on non-demo installs — same as /demo/request.
  const info = useQuery({
    queryKey: ['demo-info'],
    queryFn: demo.info,
    retry: false,
  });
  useEffect(() => {
    if (info.isFetched && !info.data) navigate({ to: '/login' });
    if (info.data && info.data.mode !== 'request_access') navigate({ to: '/login' });
  }, [info.isFetched, info.data, navigate]);

  const login = useMutation({
    mutationFn: () => demo.tokenLogin(token.trim()),
    onSuccess: () => navigate({ to: '/dashboard' }),
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('demo.login.error') as string),
      ),
  });

  // Auto-submit when arriving from an email link.
  useEffect(() => {
    if (submittedAuto.current) return;
    if (search.token && search.token.length >= 10) {
      submittedAuto.current = true;
      login.mutate();
    }
    // login.mutate is referentially stable enough for this one-shot effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.token]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;
    login.mutate();
  };

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" />
          <div className="space-y-1">
            <CardTitle className="text-xl">{t('demo.login.title')}</CardTitle>
            <CardDescription>{t('demo.login.subtitle')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="token">{t('demo.login.token_label')}</Label>
              <Input
                id="token"
                autoComplete="one-time-code"
                spellCheck={false}
                autoFocus
                required
                minLength={10}
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('demo.login.token_help')}
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={login.isPending || !token.trim()}
            >
              {login.isPending ? t('common.saving') : t('demo.login.submit')}
            </Button>
            <div className="flex flex-wrap justify-between gap-2 text-xs">
              <Button asChild type="button" variant="ghost" size="sm">
                <Link to="/demo/request">{t('demo.login.no_token_cta')}</Link>
              </Button>
              <Button asChild type="button" variant="ghost" size="sm">
                <Link to="/login">{t('common.back')}</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
